import { Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { WizardHeading } from "../../../../core/components/WizardHeading";
import { useCurriculum } from "../../../../hooks/useCurriculum";
import { StoryCollectionType } from "../../../../models/modelTypes";
import { useStoryCollection } from "../contexts/collection.context";
import { CollectionType } from "../entities/CollectionType";
import * as yup from "yup";
import { Box, FormControl, FormHelperText, Grid, InputLabel, MenuItem, TextField } from "@mui/material";
import Select from "@mui/material/Select";
import { ToggleInput } from "../../../../core/components/ToggleInput";
import { FormActions } from "../../../../core/components/FormActions";
import { PrimaryButton, SecondaryButton } from "../../../layout/Button/Button";
import { Stop } from "@mui/icons-material";
import { Alert } from "@mui/material";
import type { CardColour } from "../../../../client/api/branchedStory";
import { useAppNavigate } from "../../../../hooks/useAppNavigate";
import { useFetch } from "../../../../core/components/crud/contexts/fetch.context";

export const EditCollection = (): JSX.Element => {
    const { updateCollection } = useStoryCollection();
    const { listAll, curriculums } = useCurriculum();
    const { item } = useFetch<StoryCollectionType>();
    const [error, setError] = useState();
    const { push, goBack } = useAppNavigate();
    useEffect(() => {
        listAll();
    }, []);
    const onSubmit = (values: Partial<StoryCollectionType> & { numWeeks: number }) => {
        const updated = {
            name: values?.name,
            description: values?.description,
            ageRange: values?.ageRange,
            colour: values?.colour,
            type: values?.type,
            specialTraining: values?.specialTraining,
            curriculumId: values?.curriculumId,
            guideURL: values?.guideURL,
            guideURLROI: values?.guideURLROI,
        };
        if (item && item.id) {
            updateCollection(item.id, updated)
                .then(() => {
                    setError(undefined);
                    push(`/collections/view/${item.id}`);
                })
                .catch((error) => setError(error));
        }
    };
    const ageRanges = [
        ...(() => {
            const ageRanges: string[] = [];
            for (let i = 4; i < 17; i++) {
                ageRanges.push(`${i}-${i + 1}`);
            }
            return ageRanges;
        })(),
        "18+",
    ];
    const colourOptions = useMemo(() => {
        return [
            "Red",
            "Orange",
            "Yellow",
            "Green",
            "Cyan",
            "Blue",
            "Magneta",
            "Crimson",
            "Silver",
            "Bronze",
            "BrightYellow",
            "Aqua",
            "Purple",
            "BrightOrange",
            "BrightRed",
            "LightGreen",
            "NightBlue",
        ] as CardColour[];
    }, []);

    const availableCurriculums = useMemo(() => {
        if (curriculums && curriculums.length > 0) {
            return curriculums;
        }

        if (item?.Curriculum) {
            return [item.Curriculum];
        }

        return [];
    }, [curriculums, item?.Curriculum]);

    const initialColour = useMemo(() => {
        if (item?.colour && colourOptions.includes(item.colour)) {
            return item.colour;
        }

        return "Silver" as CardColour;
    }, [item?.colour, colourOptions]);

    const initialCurriculumId = useMemo(() => {
        if (item?.curriculumId && availableCurriculums.some((curriculum) => curriculum.id === item.curriculumId)) {
            return item.curriculumId;
        }

        return 0;
    }, [item?.curriculumId, availableCurriculums]);

    return (
        <Box p={4}>
            <form>
                <WizardHeading overline="Edit a Story Collection" heading="Collection Details" />
                <Formik
                    initialValues={{
                        name: item?.name ?? "",
                        description: item?.description ?? "",
                        ageRange: item?.ageRange ?? "",
                        colour: initialColour,
                        type: item?.type ?? CollectionType.Group,
                        specialTraining: item?.specialTraining ?? false,
                        curriculumId: initialCurriculumId,
                        numWeeks: item?.Weeks?.length ?? 1,
                        guideURL: item?.guideURL ?? "",
                        guideURLROI: item?.guideURLROI ?? "",
                    }}
                    onSubmit={onSubmit}
                    enableReinitialize={true}
                    validationSchema={yup.object({
                        name: yup.string().required("Story collection name is required"),
                        description: yup.string().required("Story collection description is required"),
                        type: yup.string().required("Collection type is required"),
                        specialTraining: yup
                            .string()
                            .required("If this collection requires special training is required"),
                        curriculumId: yup.number().integer().moreThan(0).required("Curriculum is required"),
                        guideURL: yup.string().url("This must be a valid URL"),
                        guideURLROI: yup.string().url("This must be a valid URL"),
                    })}
                    validateOnChange={false}
                >
                    {({ submitForm, errors, values, handleChange, touched, isSubmitting, setFieldValue }) => {
                        return (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        autoFocus
                                        required
                                        name="name"
                                        variant="outlined"
                                        label="Collection Name"
                                        value={values.name}
                                        fullWidth
                                        onChange={handleChange}
                                        helperText={errors.name && touched.name && errors.name}
                                        error={!!errors.name}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        required
                                        fullWidth
                                        variant="outlined"
                                        name="description"
                                        label="Collection Description"
                                        value={values.description}
                                        multiline
                                        rows={4}
                                        onChange={handleChange}
                                        helperText={errors.description && touched.description && errors.description}
                                        error={!!errors.description}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl
                                        variant="outlined"
                                        fullWidth
                                        error={!!errors.ageRange}
                                        style={{ height: "100%" }}
                                    >
                                        <InputLabel id="age-label" required>
                                            Age Range of Collection
                                        </InputLabel>
                                        <Select
                                            variant="outlined"
                                            fullWidth
                                            required
                                            label="Age Range of Collection"
                                            labelId="age-label"
                                            name="ageRange"
                                            id="ageRange"
                                            value={values.ageRange}
                                            onChange={(e) => {
                                                setFieldValue("ageRange", e.target.value);
                                            }}
                                        >
                                            <MenuItem key={0} value={""} disabled>
                                                Please select
                                            </MenuItem>
                                            {ageRanges.map((ageRange) => (
                                                <MenuItem key={ageRange} value={ageRange}>
                                                    {ageRange}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {errors.ageRange && touched.ageRange && errors.ageRange}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <FormControl variant="outlined" error={!!errors.colour} fullWidth>
                                                <InputLabel id="colour-label" required>
                                                    Card Colour
                                                </InputLabel>
                                                <Select
                                                    variant="outlined"
                                                    fullWidth
                                                    label="Card Colour"
                                                    labelId="colour-label"
                                                    name="colour"
                                                    id="colour"
                                                    value={values.colour}
                                                    onChange={(e) => {
                                                        setFieldValue("colour", e.target.value);
                                                    }}
                                                >
                                                    <MenuItem key={0} value={""} disabled>
                                                        Please select
                                                    </MenuItem>
                                                    {colourOptions.map((colourOption) => (
                                                        <MenuItem key={colourOption} value={colourOption}>
                                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                                <Stop
                                                                    fontSize="large"
                                                                    style={{
                                                                        fill: `var(--collection-colour-${colourOption.toLowerCase()}, currentColor)`,
                                                                    }}
                                                                />
                                                                {colourOption}
                                                            </div>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText>
                                                    {errors.colour && touched.colour && errors.colour}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControl variant="outlined" error={!!errors.type} fullWidth>
                                                <InputLabel id="type-label" required>
                                                    Collection Type
                                                </InputLabel>
                                                <Select
                                                    variant="outlined"
                                                    fullWidth
                                                    labelId="type-label"
                                                    name="type"
                                                    id="type"
                                                    value={values.type}
                                                    label="Collection Type"
                                                    onChange={(e) => {
                                                        setFieldValue("type", e.target.value);
                                                    }}
                                                >
                                                    {Object.entries(CollectionType).map(([value, label]) => {
                                                        return (
                                                            <MenuItem key={value} value={value}>
                                                                {label}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                                <FormHelperText>
                                                    {errors.type && touched.type && errors.type}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl variant="outlined" error={!!errors.curriculumId} fullWidth>
                                        <InputLabel id="curriculum-label" required>
                                            Choose a curriculum
                                        </InputLabel>
                                        <Select
                                            variant="outlined"
                                            fullWidth
                                            label="Choose a curriculum"
                                            name="curriculumId"
                                            value={values.curriculumId}
                                            id="curriculumId"
                                            labelId="curriculum-label"
                                            onChange={(e) => {
                                                setFieldValue("curriculumId", e.target.value);
                                            }}
                                        >
                                            <MenuItem key={0} value={""} disabled>
                                                Please select
                                            </MenuItem>
                                            {availableCurriculums.map((curriculum) => (
                                                <MenuItem key={curriculum.id} value={curriculum.id}>
                                                    {curriculum.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {errors.curriculumId && touched.curriculumId && errors.curriculumId}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        onChange={handleChange}
                                        name="guideURL"
                                        label="Teacher Guide Google Drive URL - NI"
                                        value={values.guideURL}
                                        helperText={errors.guideURL && touched.guideURL && errors.guideURL}
                                        error={!!errors.guideURL}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        onChange={handleChange}
                                        name="guideURLROI"
                                        label="Teacher Guide Google Drive URL - ROI"
                                        value={values.guideURLROI}
                                        helperText={errors.guideURLROI && touched.guideURLROI && errors.guideURLROI}
                                        error={!!errors.guideURLROI}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl variant="outlined" fullWidth>
                                        <InputLabel id="numWeeks-label" required>
                                            Num. of Weeks
                                        </InputLabel>
                                        <Select
                                            variant="outlined"
                                            fullWidth
                                            label="Number of Weeks"
                                            labelId="numWeeks-label"
                                            name="numWeeks"
                                            id="numWeeks"
                                            value={values.numWeeks}
                                            onChange={handleChange}
                                        >
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <ToggleInput
                                        id="specialTraining"
                                        value={values.specialTraining ?? false}
                                        setFieldValue={setFieldValue}
                                        label="Does this collection require specialised training?"
                                        leftOption="Yes"
                                        rightOption="No"
                                    />
                                </Grid>
                                <Grid item xs={12} style={{ color: "rgba(0, 0, 0, 0.54)" }}>
                                    Input fields marked with an asterisk (*) are required
                                </Grid>
                                {error && (
                                    <Grid item xs={12}>
                                        <Box mb={2}>
                                            <Alert severity="error">{error}</Alert>
                                        </Box>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <FormActions
                                        left={
                                            <SecondaryButton size="large" onClick={() => goBack()}>
                                                Back
                                            </SecondaryButton>
                                        }
                                        right={
                                            <>
                                                <PrimaryButton
                                                    size="large"
                                                    disabled={isSubmitting}
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        submitForm();
                                                    }}
                                                >
                                                    Save
                                                </PrimaryButton>
                                            </>
                                        }
                                    />
                                </Grid>
                            </Grid>
                        );
                    }}
                </Formik>
            </form>
        </Box>
    );
};
